import ApplicationController from "../../application_component_controller"

import * as echarts from 'echarts';
import tippy, {followCursor} from 'tippy.js';
import moment from 'moment-timezone';
import { useResize } from "hb/insights/use_resize"
import { useOutlets } from "hb/insights/use_outlets"
import { fetchJSON } from "hb/lib/fetch"
import URL from "hb/lib/url"
import { userTZ } from "hb/lib/date_helpers"
import { loadConfig } from "hb/insights/trigger_configs"
import Temporal from "hb/time/temporal"

export default class extends ApplicationController {
  static targets = ["chart", "triggers", "triggerHover"]
  static values = {
    summaryPath: String,
    previewPath: String,
    queryPath: String,
    observer: Object,
    showChart: Boolean,
    resizeObserverSelector: String,
  }

  connect() {
    useOutlets(this, {
      components: ["ballBounceLoader"],
      scope: this.element
    })

    if (!this.showChartValue) return;

    this.triggerHoverTemplate = this.compileElement(this.triggerHoverTarget)
    this.chart = echarts.init(this.chartTarget, null, {
      useCoarsePointer: true
    });
    this.afterRender = false;
    this.element.classList.add("loading")

    this.chart.on("rendered", () => {
      if (this.afterRender) {
        this.afterRender = false;
        var gridModel = this.chart.getModel().getComponent('grid', 0);

        var gridRect = gridModel.coordinateSystem.getRect();
        var containerStyle = this.chart.getDom().getBoundingClientRect();

        var gridLeft = (gridRect.x / containerStyle.width) * 100;
        var gridWidth = (gridRect.width / containerStyle.width) * 100;

        this.triggersTarget.style.marginLeft = gridLeft + '%';
        this.triggersTarget.style.width = gridWidth + '%';
      }
    })

    if(this.hasResizeObserverSelectorValue) {
      useResize(this, {element: document.querySelector(this.resizeObserverSelectorValue)})
    } else {
      useResize(this, {element: this.chartTarget})
    }
    this.observer = JSON.parse(JSON.stringify(this.observerValue))

    this.urlChangedHandler = this.urlChanged.bind(this)
    window.addEventListener("urlchanged", this.urlChangedHandler)

    const configClass = loadConfig(this.observer.trigger_config?.type)
    if (configClass) {
      this.triggerConfig = new configClass(this.observer)
    }
  }

  updateTriggerConfig(name, value) {
    if (!this.showChartValue) return;

    if (name === "alarm[trigger_config][config][operator]") {
      this.observer.trigger_config.config.operator = value
    } else {
      this.observer.trigger_config.config.value = parseInt(value, 10)
    }

    const configClass = loadConfig(this.observer?.trigger_config?.type)
    if (configClass) {
      this.triggerConfig = new configClass(this.observer)
    }

    this.renderChart()
  }

  disconnect() {
    if (!this.showChartValue) return;

    this.disconnected = true;
    window.removeEventListener("urlchanged", this.urlChangedHandler)

    if(this.chart) {
      echarts.dispose(this.chart)
    }
  }

  outletsConnected() {
    if (!this.showChartValue) return;

    this.request()
  }

  urlChanged() {
    const params = URL.params()
    const tsChanged = params.has("ts") && this.ts !== undefined && this.ts !== params.get("ts")
    const tzChanged = this.timezone !== params.get("timezone")

    if (tsChanged || tzChanged) {
      this.request()
    }
  }

  request() {
    this.outletComponent("ballBounceLoader").start()

    let timezone
    const urlParams = URL.params()
    if(urlParams.has("timezone")) {
      timezone = this.timezone = urlParams.get("timezone")
    } else {
      timezone = this.timezone = userTZ()
    }

    const ts = this.ts = urlParams.get("ts") || "PT1H"

    let opts;
    if (this.hasPreviewPathValue) {
      opts = {
        path: this.previewPathValue,
        jsonBody: { alarm: this.observerValue, ts, timezone },
        method: "POST"
      }
    } else {
      opts = {
        path: this.summaryPathValue,
        params: { ts, timezone },
        method: "GET"
      }
    }

    fetchJSON({...opts, camelizeResponseBody: true, timeout: 60000}).then((data) => {
      this.previewData = data;
      this.render();
    })
  }

  renderTriggers() {
    const {startAt, endAt, triggers} = this.previewData
    this.triggersTarget.innerHTML = ""

    // Give me a set of divs that line up based on the evaluationStartAt of subsequent triggers
    const start = moment.tz(startAt, this.timezone).unix()
    const end = moment.tz(endAt, this.timezone).unix()

    triggers?.forEach(trigger => {
      trigger.evaluationStartedAtTime = moment(trigger.evaluationStartedAt).tz(this.timezone),
      trigger.evaluationStartedAtTimeUTC = moment(trigger.evaluationStartedAt),
      trigger.evaluationStartedAtTemporal = new Temporal(trigger.evaluationStartedAtTime),
      trigger.evaluationStartedAtTz = trigger.evaluationStartedAtTime.toString(),
      trigger.evaluationStartedAtSeconds = trigger.evaluationStartedAtTime.unix()
    })

    // Attempt to find any triggers that are overlapping from differing version
    // and remove them
    let coll = []
    let current = false;
    for (let i = 0; i < triggers?.length || 0; i++) {
      const t = triggers[i]
      const nextT = triggers[i + 1] || {observerId: null, evaluationStartedAtSeconds: 0}

      if (t.observerId === this.observer.id && current === false) {
        current = true;
      }

      if (t.status === "not-started-yet" || t.status === "waiting") {
        coll.push(t)
      } else if (current && t.observerId !== this.observer.id) {
        // This is a trigger from a previous version, ahead of the current version, skip
      } else if (t.evaluationStartedAtSeconds === nextT.evaluationStartedAtSeconds && t.observerId !== this.observer.id) {
        // This is a trigger overlapping the current version, skip
      } else {
        coll.push(t)
      }
    }

    for (let i = 0; i < coll?.length || 0; i++) {
      const startTrigger = coll[i]
      const endTrigger = coll[i + 1]  || {evaluationStartedAtSeconds: end}
      const triggerStart = Math.max(start, startTrigger.evaluationStartedAtSeconds)
      const triggerEnd = endTrigger.evaluationStartedAtSeconds

      const triggerDiv = document.createElement("div")
      triggerDiv.className = `trigger trigger-${startTrigger.status}`
      triggerDiv.style.width = `${(triggerEnd - triggerStart) / (end - start) * 100}%`
      triggerDiv.style.left = `${(triggerStart - start) / (end - start) * 100}%`

      const duration = startTrigger.evaluationStartedAtTime.from(endTrigger.evaluationStartedAtTime)

      let params = URL.slice(["timezone"]);
      let ts = startTrigger.evaluationStartedAtTemporal.param;
      if (endTrigger.evaluationStartedAtTime) {
        ts += `/${endTrigger.evaluationStartedAtTemporal.param}`
      }
      params.set("ts", ts)
      params.set("query", this.observer.query)
      let link = `${this.queryPathValue}?${params.toString()}`

      let content;
      if (startTrigger.status === "not-started-yet") {
        content = "Alarm not started yet"
      } else if (startTrigger.status === "waiting") {
        content = "Waiting"
      } else {
        content = this.triggerHoverTemplate({
          status: startTrigger.status,
          value: startTrigger.evaluationResult,
          link,
          duration
        })
      }

      tippy(triggerDiv, {
        content,
        interactive: true,
        placement: "bottom",
        maxWidth: 250,
        hideOnClick: false,
        appendTo: document.body,
        followCursor: "horizontal",
        allowHTML: true,
        plugins: [followCursor]
      })

      this.triggersTarget.appendChild(triggerDiv)
    }
  }

  renderChart() {
    let {startAt, endAt, values} = this.previewData

    // Attempt to find any values that are overlapping from differing versions
    // and remove them
    let coll = []
    let current = false;
    for (let i = 0; i < values?.length || 0; i++) {
      const val = values[i]
      const nextVal = values[i + 1] || [null, null, null]

      if (val[2] === this.observer.id && current === false) {
        current = true;
      }

      if (current && val[2] !== this.observer.id) {
        // This is a value from a previous version, ahead of the current version, skip
      } else if (val[0] === nextVal[0] && val[2] !== this.observer.id) {
        // This is a value overlapping the current version, skip
      } else {
        coll.push(val)
      }
    }

    const options = {
      tooltip: {
        trigger: 'axis',
      },
      grid: {
        borderWidth: 0,
        left: 0,
        bottom: 5,
        right: 0,
        top: 10,
        show: true,
        containLabel: true
      },
      xAxis: {
        type: 'time',
        min: startAt,
        max: endAt
      },
      yAxis: {
        name: ""
      },
      series: [{
        type: 'line',
        data: coll,
        animation: false,
        lineStyle: { width: 1.5 },
        showSymbol: true,
        symbolSize: 3,
        emphasis: {
          scale: true,
          focus: 'series'
        },
        markArea: {
          silent: true,
          label: {show: true},
          itemStyle: { color: 'rgb(255 0 0 / 20%)' },
          ...this.triggerConfig?.markAreaConfig(this.previewData)
        },
        markLine: {
          silent: false,  // true if you do not want the line to respond to mouse events
          symbol: ['none', 'none'],
          label: {show: false},
          ...this.triggerConfig?.markLineConfig(this.previewData)
        },
        blur: {
          lineStyle: { opacity: 0.4 }
        }
      }]
    }

    this.afterRender = true;
    this.chart.setOption(options);
  }

  render() {
    // Bail if we've been disconnected
    if(this.outletComponent("ballBounceLoader") === null) return;

    this.outletComponent("ballBounceLoader").stop()

    if (this.hasPreviewPathValue) {
      this.renderChart()
    } else {
      this.renderTriggers()
      this.renderChart()
    }
  }

  resize() {
    this.chart.resize()
  }
}
